import { useOrganization, useUser } from "@clerk/react";
import { PlusIcon, UsersIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearch } from "@tanstack/react-router";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { useI18n } from "~/i18n";
import {
  addInvitedPerson,
  isInviteEmail,
  removeInvitedPerson,
  useOnboardingProfile,
} from "../../onboardingStore";
import {
  createOrgMember,
  fetchOrgMembers,
  syncOrgSeats,
  updateOrgMember,
  updateMyOrgMemberProfile,
  type MemberGrants,
  type OrgClerkRole,
  type OrgMember,
  type OrgMemberInput,
} from "../../orgMembers";
import { useEntitlements } from "../../morrowEntitlements";
import { openUpgradeModal } from "../../upgradeModal";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "../ui/popover";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import {
  MorrowPersonCard,
  MorrowPersonDetailCard,
  clerkPortraitImageUrl,
  personRoleLine,
  type MorrowPersonDetailInfo,
} from "./MorrowPersonCard";
import { MorrowTitleRoleForm } from "./MorrowTitleRoleForm";
import { MorrowPersonAvatarPicker } from "./MorrowPersonAvatarPicker";
import {
  buildClerkMemberAvatarMap,
  isSelfMember,
  resolveClerkPortrait,
  resolveMemberPersonAvatar,
  resolveSelfAvatarSeed,
} from "./morrowMemberAvatarResolve";
import {
  defaultMorrowDicebearAvatarSeed,
  normalizeMorrowDicebearAvatarSeed,
} from "./morrowPersonAvatarCatalog";
import { TeamHierarchyView } from "./TeamHierarchyView";
import { MorrowWorkspacePage } from "./MorrowWorkspacePage";
import {
  canEditTeamMember,
  isTeamClerkDirectoryLoading,
  isTeamOrgDirectoryBacked,
  isTeamOrgDirectoryLoading,
  isWorkspaceTeamAdmin,
  mergeTeamOrgDirectory,
} from "./MorrowTeamPage.logic";
import { upsertTeamMember, useTeamState, type MorrowTeamMember } from "./morrowTeamStore";
import {
  LOCAL_SELF_MEMBER_ID,
  ensureUserAvatarSeed,
  metadataFromUserProfile,
  useUserProfile,
  writeUserProfile,
  type MorrowUserProfile,
} from "./morrowUserProfileStore";

/** The rich fields the add-member popover collects, plus the Clerk role. */
export type AddMemberFields = OrgMemberInput;

/** A member edit: any rich field plus, admin-only, the per-member grant toggles. */
export type MemberEditPatch = Partial<Omit<OrgMemberInput, "email">> & {
  readonly grants?: Partial<MemberGrants>;
};

/**
 * Did an invite fail because the org is out of seats? Catches BOTH our server's
 * 402 seat backstop and Clerk's native `maxAllowedMemberships` rejection (whose
 * error shape/codes vary), so either path surfaces the upgrade card, not a raw error.
 */
export function isSeatLimitError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as {
    status?: number;
    message?: string;
    errors?: ReadonlyArray<{ code?: string; message?: string }>;
  };
  if (e.status === 402) return true;
  const hay = [
    e.message ?? "",
    ...(Array.isArray(e.errors) ? e.errors.flatMap((x) => [x.code ?? "", x.message ?? ""]) : []),
  ]
    .join(" ")
    .toLowerCase();
  return /quota|membership|max.?allowed|seat|maximum|limit reached/.test(hay);
}

export function MorrowTeamPage() {
  if (hasClerkPublishableKey()) return <ClerkMorrowTeamPage />;
  return <MorrowTeamPageBody displayName={null} isAdmin />;
}

function ClerkMorrowTeamPage() {
  const { user } = useUser();
  const { isLoaded, organization, membership, memberships, invitations } = useOrganization({
    memberships: { pageSize: 50 },
    invitations: { pageSize: 50 },
  });
  const [orgMembers, setOrgMembers] = useState<OrgMember[] | null>(null);

  const displayName =
    user?.fullName?.trim() ||
    user?.firstName?.trim() ||
    user?.primaryEmailAddress?.emailAddress ||
    null;
  const isAdmin = isWorkspaceTeamAdmin({
    orgLoaded: isLoaded,
    clerkEnabled: true,
    organizationId: organization?.id ?? null,
    orgRole: membership?.role ?? null,
  });
  const memberAvatars = useMemo(
    () => buildClerkMemberAvatarMap(memberships?.data, user),
    [memberships?.data, user],
  );

  // The DB-backed rich member directory (titles/roles/etc.), loaded from the
  // server for the caller's active org. Null until the first load resolves; an
  // empty array means "loaded, no members".
  const refreshMembers = useCallback(async () => {
    if (!organization) {
      setOrgMembers(null);
      return;
    }
    const rows = await fetchOrgMembers();
    // Always resolve the loading state — a null response (403, network, etc.)
    // means "no DB directory for this caller", not "still loading".
    setOrgMembers(rows ?? []);
  }, [organization]);

  useEffect(() => {
    void refreshMembers();
  }, [refreshMembers]);

  useEffect(() => {
    if (organization && isAdmin) void syncOrgSeats();
  }, [organization, isAdmin]);

  const organizationActive = isLoaded && organization != null;
  const orgLoading = Boolean(organization) && (memberships?.isLoading ?? false);
  const orgJoinedCount = organization?.membersCount ?? memberships?.data?.length ?? 0;
  const selfImageUrl = clerkPortraitImageUrl(user);
  const selfEmail = user?.primaryEmailAddress?.emailAddress?.trim() ?? "";

  return (
    <MorrowTeamPageBody
      displayName={displayName}
      isAdmin={isAdmin}
      organizationActive={organizationActive}
      orgLoading={orgLoading}
      orgJoinedCount={orgJoinedCount}
      memberAvatars={memberAvatars}
      selfUserId={user?.id ?? null}
      selfEmail={selfEmail}
      {...(selfImageUrl ? { selfImageUrl } : {})}
      orgMembers={orgMembers}
      clerkMemberships={memberships?.data}
      clerkInvitations={invitations?.data}
      clerkMembershipsLoading={memberships?.isLoading ?? false}
      onAddMember={async (fields) => {
        // The backend now owns BOTH the authoritative Clerk invite (with a
        // redirectUrl so the email opens our app, not Clerk's hosted portal) AND
        // the rich domain fields — the client inviteMember cannot set a redirect.
        await createOrgMember(fields);
        await refreshMembers();
      }}
      onEditMember={async (email, patch) => {
        await updateOrgMember(email, patch);
        await refreshMembers();
      }}
      onSyncSelfProfile={async (profile) => {
        if (!user) return;
        await user.update({
          unsafeMetadata: metadataFromUserProfile({ ...user.unsafeMetadata }, profile),
        });
        await updateMyOrgMemberProfile({
          avatarSeed: profile.avatarSeed || defaultMorrowDicebearAvatarSeed(displayName ?? ""),
          title: profile.title,
          functionRole: profile.role,
        });
        await refreshMembers();
      }}
    />
  );
}

function MorrowTeamPageBody({
  displayName,
  isAdmin,
  organizationActive = false,
  orgLoading = false,
  orgJoinedCount = 0,
  orgMembers,
  clerkMemberships,
  clerkInvitations,
  clerkMembershipsLoading = false,
  memberAvatars,
  selfImageUrl,
  selfUserId = null,
  selfEmail = "",
  onAddMember,
  onEditMember,
  onSyncSelfProfile,
}: {
  readonly displayName: string | null;
  readonly isAdmin: boolean;
  readonly organizationActive?: boolean;
  readonly orgLoading?: boolean;
  readonly orgJoinedCount?: number;
  readonly orgMembers?: OrgMember[] | null;
  readonly clerkMemberships?:
    | ReadonlyArray<{
        readonly publicUserData?: {
          readonly firstName?: string | null;
          readonly lastName?: string | null;
          readonly identifier?: string | null;
          readonly userId?: string | null;
        } | null;
        readonly role?: string | null;
      }>
    | undefined;
  readonly clerkInvitations?:
    | ReadonlyArray<{
        readonly emailAddress?: string | null;
        readonly role?: string | null;
        readonly status?: string | null;
      }>
    | undefined;
  readonly clerkMembershipsLoading?: boolean;
  readonly memberAvatars?: ReadonlyMap<string, string>;
  readonly selfImageUrl?: string;
  readonly selfUserId?: string | null;
  readonly selfEmail?: string;
  readonly onAddMember?: (fields: AddMemberFields) => Promise<void>;
  readonly onEditMember?: (email: string, patch: MemberEditPatch) => Promise<void>;
  readonly onSyncSelfProfile?: (profile: MorrowUserProfile) => Promise<void>;
}) {
  const { t } = useI18n();
  const { member: memberFromSearch } = useSearch({ strict: false });
  const profile = useOnboardingProfile();
  const userProfile = useUserProfile();
  const { userId } = useMorrowClerkAuth();
  const team = useTeamState();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [functionRole, setFunctionRole] = useState("");
  const [description, setDescription] = useState("");
  const [role, setRole] = useState<OrgClerkRole>("org:member");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitePending, setInvitePending] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedOrgEmail, setSelectedOrgEmail] = useState<string | null>(null);
  const selfId = userId ?? LOCAL_SELF_MEMBER_ID;
  const selfName = displayName?.trim() || t("team.you");
  const resolveAvatar = (key: string) => resolveClerkPortrait(memberAvatars, key);
  const resolvedSelfAvatarSeed = resolveSelfAvatarSeed(userProfile.avatarSeed, selfName);
  const company = profile.companyName.trim() || t("team.untitled");
  // When the DB directory is available (Clerk org mode) we render members from
  // it; otherwise we fall back to the local team store.
  const orgMembersLoading = isTeamOrgDirectoryLoading({ organizationActive, orgMembers });
  const orgBacked = isTeamOrgDirectoryBacked({ organizationActive, orgMembers });
  const clerkDirectoryLoading = isTeamClerkDirectoryLoading({
    organizationActive,
    orgBacked,
    clerkMembershipsLoading,
  });
  const mergedOrgDirectory = useMemo(
    () =>
      mergeTeamOrgDirectory({
        dbMembers: orgMembers ?? [],
        clerkMemberships,
        clerkInvitations,
      }),
    [clerkInvitations, clerkMemberships, orgMembers],
  );
  const entitlements = useEntitlements();
  const seatLimit = orgBacked ? entitlements.limit("seats") : null;
  const pendingInviteCount = mergedOrgDirectory.invitedMembers.length;
  const seatsUsed = orgBacked ? orgJoinedCount + pendingInviteCount : 0;
  const seatsFull = seatLimit !== null && seatsUsed >= seatLimit;
  const openSeatUpgrade = () => openUpgradeModal({ capability: "seats" });

  useEffect(() => {
    if (!memberFromSearch) return;
    if (orgBacked) {
      setSelectedOrgEmail(memberFromSearch);
      return;
    }
    setSelectedId(memberFromSearch);
  }, [memberFromSearch, orgBacked]);

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  useEffect(() => {
    upsertTeamMember({
      id: selfId,
      name: selfName,
      title: userProfile.title,
      role: userProfile.role,
      avatarSeed: userProfile.avatarSeed || resolvedSelfAvatarSeed,
    });
  }, [
    selfId,
    selfName,
    userProfile.role,
    userProfile.title,
    userProfile.avatarSeed,
    resolvedSelfAvatarSeed,
  ]);

  const pendingInvites = useMemo(
    () =>
      profile.invitedPeople.filter(
        (person) =>
          !team.members.some(
            (member) => member.id === person.email || member.name === person.email,
          ),
      ),
    [profile.invitedPeople, team.members],
  );

  const joinedOrgMembers = mergedOrgDirectory.joinedMembers;
  const invitedOrgMembers = mergedOrgDirectory.invitedMembers;

  const selected =
    team.members.find((member) => member.id === selectedId) ??
    (selectedId === selfId
      ? {
          id: selfId,
          name: selfName,
          title: userProfile.title,
          role: userProfile.role,
          avatarSeed: resolvedSelfAvatarSeed,
        }
      : null);
  const selectedOrgMember =
    selectedOrgEmail != null
      ? ([...joinedOrgMembers, ...invitedOrgMembers].find(
          (member) => member.email.trim().toLowerCase() === selectedOrgEmail.trim().toLowerCase(),
        ) ?? null)
      : null;

  useEffect(() => {
    if (!selectedOrgEmail || selectedOrgMember) return;
    if (orgBacked && joinedOrgMembers.length + invitedOrgMembers.length > 0) {
      setSelectedOrgEmail(null);
    }
  }, [
    invitedOrgMembers.length,
    joinedOrgMembers.length,
    orgBacked,
    selectedOrgEmail,
    selectedOrgMember,
  ]);

  const resetForm = () => {
    setEmail("");
    setTitle("");
    setFunctionRole("");
    setDescription("");
    setRole("org:member");
    setInviteError(null);
  };

  const handleInvite = async (event: FormEvent) => {
    event.preventDefault();
    const nextEmail = email.trim().toLowerCase();
    if (!isInviteEmail(nextEmail)) {
      setInviteError(t("onboarding.inviteError"));
      return;
    }
    // Keep the local pending record so the invite shows immediately even before
    // the server round-trips.
    addInvitedPerson(nextEmail);
    if (onAddMember) {
      setInvitePending(true);
      try {
        await onAddMember({
          email: nextEmail,
          role,
          title: title.trim(),
          functionRole: functionRole.trim(),
          description: description.trim(),
          defaultSensitivity: "normal",
        });
      } catch (err) {
        if (isSeatLimitError(err)) {
          removeInvitedPerson(nextEmail);
          setInvitePending(false);
          setOpen(false);
          openSeatUpgrade();
          return;
        }
        // Otherwise best-effort: the local record stays; server is the source of truth.
      } finally {
        setInvitePending(false);
      }
    }
    resetForm();
    setOpen(false);
  };

  return (
    <MorrowWorkspacePage
      title={t("team.title")}
      subtitle={t("team.subtitle", { name: company })}
      icon={<UsersIcon className="size-4" />}
      wide
      action={
        <div className="flex items-center gap-2">
          {orgBacked && seatLimit !== null ? (
            <span className="text-xs tabular-nums text-muted-foreground">
              {t("team.seatsUsage", { used: seatsUsed, limit: seatLimit })}
            </span>
          ) : null}
          {seatsFull ? (
            <Button size="sm" onClick={openSeatUpgrade}>
              <PlusIcon />
              {t("team.addMember")}
            </Button>
          ) : (
            <Popover
              open={open}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) resetForm();
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-sky-500 [--control-icon-color:var(--color-sky-500)] hover:bg-sky-500/10 hover:text-sky-500"
                  >
                    <PlusIcon />
                    {t("team.addMember")}
                  </Button>
                }
              />
              <PopoverPopup align="end" className="w-80" side="bottom">
                <form
                  className="flex flex-col gap-3"
                  onSubmit={(event) => void handleInvite(event)}
                >
                  <PopoverTitle className="text-sm">{t("team.addMember")}</PopoverTitle>
                  <p className="text-sm text-muted-foreground">{t("team.joinHint")}</p>
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("team.joinEmail")}
                    </span>
                    <Input
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.currentTarget.value);
                        if (inviteError) setInviteError(null);
                      }}
                      placeholder={t("team.emailPlaceholder")}
                      required
                    />
                  </label>
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("team.joinTitle")}
                    </span>
                    <Input
                      type="text"
                      value={title}
                      onChange={(event) => setTitle(event.currentTarget.value)}
                      placeholder={t("team.titlePlaceholder")}
                    />
                  </label>
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("team.joinFunction")}
                    </span>
                    <Input
                      type="text"
                      value={functionRole}
                      onChange={(event) => setFunctionRole(event.currentTarget.value)}
                      placeholder={t("team.functionPlaceholder")}
                    />
                  </label>
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("team.joinDescription")}
                    </span>
                    <Textarea
                      value={description}
                      onChange={(event) => setDescription(event.currentTarget.value)}
                      placeholder={t("team.descriptionPlaceholder")}
                      rows={2}
                    />
                  </label>
                  <label className="flex min-w-0 flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("team.joinAccess")}
                    </span>
                    <Select
                      value={role}
                      onValueChange={(value: string | null) => {
                        if (value === "org:admin" || value === "org:member") setRole(value);
                      }}
                      items={[
                        { value: "org:member", label: t("team.roleMember") },
                        { value: "org:admin", label: t("team.roleAdmin") },
                      ]}
                    >
                      <SelectTrigger size="sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectPopup>
                        <SelectItem value="org:member">{t("team.roleMember")}</SelectItem>
                        <SelectItem value="org:admin">{t("team.roleAdmin")}</SelectItem>
                      </SelectPopup>
                    </Select>
                  </label>
                  {inviteError ? (
                    <p className="text-xs text-error-foreground">{inviteError}</p>
                  ) : null}
                  <div>
                    <Button disabled={invitePending} type="submit" size="sm">
                      {invitePending ? t("common.loading") : t("team.join")}
                    </Button>
                  </div>
                </form>
              </PopoverPopup>
            </Popover>
          )}
        </div>
      }
    >
      {orgMembersLoading || clerkDirectoryLoading || (orgLoading && !orgBacked) ? (
        <TeamSkeleton />
      ) : orgBacked ? (
        joinedOrgMembers.length === 0 && invitedOrgMembers.length === 0 ? (
          <TeamEmptyState
            title={t("team.empty")}
            description={t("team.emptyHint")}
            canAdd={isAdmin && !!onAddMember}
            addLabel={t("team.addMember")}
            onAdd={() => setOpen(true)}
          />
        ) : (
          <TeamHierarchyView
            joinedMembers={joinedOrgMembers}
            invitedMembers={invitedOrgMembers}
            resolveAvatar={resolveAvatar}
            selfUserId={selfUserId}
            selfEmail={selfEmail}
            selfAvatarSeed={resolvedSelfAvatarSeed}
            selfTitle={userProfile.title}
            selfRole={userProfile.role}
            onOpenMember={(member) => setSelectedOrgEmail(member.email)}
          />
        )
      ) : team.members.length === 0 && pendingInvites.length === 0 ? (
        <TeamEmptyState
          title={t("team.empty")}
          description={t("team.emptyHint")}
          canAdd={isAdmin && !!onAddMember}
          addLabel={t("team.addMember")}
          onAdd={() => setOpen(true)}
        />
      ) : (
        <div className="@container flex flex-col gap-8">
          {team.members.length > 0 ? (
            <section className="flex min-w-0 flex-col gap-3">
              <h2 className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
                {t("team.members")}
              </h2>
              <ul className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3 @[56rem]:grid-cols-4">
                {team.members.map((member) => {
                  const isSelf = member.id === selfId;
                  const memberTitle = isSelf ? userProfile.title : member.title;
                  const memberRole = isSelf ? userProfile.role : member.role;
                  const memberImageUrl = isSelf
                    ? userProfile.portraitImageUrl || undefined
                    : (resolveAvatar(member.id) ?? resolveAvatar(member.name));
                  const memberAvatarSeed = isSelf ? resolvedSelfAvatarSeed : member.avatarSeed;
                  return (
                    <li key={member.id} className="min-w-0">
                      <TeamMemberCard
                        name={member.name}
                        title={memberTitle}
                        role={memberRole}
                        {...(memberImageUrl ? { imageUrl: memberImageUrl } : {})}
                        {...(memberAvatarSeed ? { avatarSeed: memberAvatarSeed } : {})}
                        isSelf={isSelf}
                        onOpen={() => setSelectedId(member.id)}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {pendingInvites.length > 0 ? (
            <section className="flex min-w-0 flex-col gap-3">
              <h2 className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
                {t("team.invited")}
              </h2>
              <ul className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3 @[56rem]:grid-cols-4">
                {pendingInvites.map((person) => (
                  <li key={person.email} className="min-w-0">
                    <MorrowPersonCard
                      pending
                      name={person.email}
                      title={person.title}
                      role={person.role}
                      description={
                        personRoleLine(person.title, person.role) || t("team.pendingInvite")
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}

      <MemberDetailDialog
        member={selected}
        isSelf={selected?.id === selfId}
        isAdmin={isAdmin}
        selfName={selfName}
        {...(() => {
          const detailImageUrl =
            selected?.id === selfId
              ? userProfile.portraitImageUrl || undefined
              : resolveAvatar(selected?.id ?? "");
          const detailAvatarSeed =
            selected?.id === selfId ? resolvedSelfAvatarSeed : selected?.avatarSeed;
          return {
            ...(detailImageUrl ? { imageUrl: detailImageUrl } : {}),
            ...(detailAvatarSeed ? { avatarSeed: detailAvatarSeed } : {}),
          };
        })()}
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelectedId(null);
        }}
        {...(onSyncSelfProfile ? { onSyncSelfProfile } : {})}
      />

      <OrgMemberDetailDialog
        member={selectedOrgMember}
        isAdmin={isAdmin}
        isSelf={
          selectedOrgMember != null &&
          (selectedOrgMember.userId === selfUserId ||
            selectedOrgMember.email.trim().toLowerCase() === selfEmail.trim().toLowerCase())
        }
        selfName={selfName}
        selfAvatarSeed={resolvedSelfAvatarSeed}
        {...(() => {
          if (!selectedOrgMember) return {};
          const isSelfMember =
            selectedOrgMember.userId === selfUserId ||
            selectedOrgMember.email.trim().toLowerCase() === selfEmail.trim().toLowerCase();
          const detailImageUrl = isSelfMember
            ? userProfile.portraitImageUrl || undefined
            : (resolveAvatar(selectedOrgMember.email) ??
              (selectedOrgMember.userId ? resolveAvatar(selectedOrgMember.userId) : undefined));
          const detailAvatarSeed = isSelfMember
            ? resolvedSelfAvatarSeed
            : selectedOrgMember.avatarSeed.trim() || undefined;
          return {
            ...(detailImageUrl ? { imageUrl: detailImageUrl } : {}),
            ...(detailAvatarSeed ? { avatarSeed: detailAvatarSeed } : {}),
          };
        })()}
        open={selectedOrgMember !== null}
        onOpenChange={(next) => {
          if (!next) setSelectedOrgEmail(null);
        }}
        {...(onEditMember ? { onEditMember } : {})}
        {...(onSyncSelfProfile ? { onSyncSelfProfile } : {})}
      />
    </MorrowWorkspacePage>
  );
}

function TeamSkeleton() {
  return (
    <div className="@container flex flex-col gap-3" aria-hidden>
      <Skeleton className="h-3 w-20 rounded-full" />
      <div className="grid grid-cols-1 gap-3 @[22rem]:grid-cols-2 @[40rem]:grid-cols-3 @[56rem]:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 p-3"
          >
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3 w-2/3 rounded-full" />
              <Skeleton className="h-2.5 w-1/2 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamEmptyState({
  title,
  description,
  canAdd,
  addLabel,
  onAdd,
}: {
  readonly title: string;
  readonly description: string;
  readonly canAdd: boolean;
  readonly addLabel: string;
  readonly onAdd: () => void;
}) {
  return (
    <section className="card-glass flex flex-col items-center overflow-hidden rounded-2xl border border-border/60 px-6 py-14 text-center">
      <div className="relative mb-5 flex size-14 items-center justify-center">
        <span className="absolute inset-0 rounded-2xl bg-primary/10" aria-hidden />
        <UsersIcon className="relative size-6 text-primary" />
      </div>
      <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 max-w-sm text-sm leading-6 text-muted-foreground">{description}</p>
      {canAdd ? (
        <Button size="sm" className="mt-5" onClick={onAdd}>
          <PlusIcon />
          {addLabel}
        </Button>
      ) : null}
    </section>
  );
}

function TeamMemberCard({
  name,
  title,
  role,
  imageUrl,
  avatarSeed,
  isSelf,
  onOpen,
}: {
  readonly name: string;
  readonly title: string;
  readonly role: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly isSelf: boolean;
  readonly onOpen: () => void;
}) {
  const { t } = useI18n();
  const description = isSelf ? t("team.profileHint") : t("team.profilePending");

  return (
    <MorrowPersonCard
      name={name}
      title={title}
      role={role}
      {...(imageUrl ? { imageUrl } : {})}
      {...(avatarSeed ? { avatarSeed } : {})}
      description={description}
      ariaLabel={t("team.openDetail", { name })}
      onClick={onOpen}
    />
  );
}

/**
 * Admin-only per-member access grants. Each toggle flips one capability grant on
 * the member; the parent dialog saves them together with the title/role edit. An
 * org admin already holds every capability, so these grants matter for members.
 */
function MemberGrantToggles({
  grants,
  onChange,
}: {
  readonly grants: MemberGrants;
  readonly onChange: (next: MemberGrants) => void;
}) {
  const { t } = useI18n();
  const rows: ReadonlyArray<{ key: keyof MemberGrants; label: string }> = [
    { key: "vaultManage", label: t("team.grant.vaultManage") },
    { key: "vaultRequest", label: t("team.grant.vaultRequest") },
    { key: "billingAccess", label: t("team.grant.billingAccess") },
    { key: "orgKbAccess", label: t("team.grant.orgKbAccess") },
  ];
  return (
    <section className="flex min-w-0 flex-col gap-3 pe-8">
      <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {t("team.grant.title")}
      </h3>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-foreground">{row.label}</span>
            <Switch
              checked={grants[row.key]}
              onCheckedChange={(checked) => onChange({ ...grants, [row.key]: Boolean(checked) })}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Admin-editable detail for a DB-backed org member (PATCHes rich fields). */
function OrgMemberDetailDialog({
  member,
  isAdmin,
  isSelf = false,
  selfName,
  imageUrl,
  avatarSeed,
  selfAvatarSeed,
  open,
  onOpenChange,
  onEditMember,
  onSyncSelfProfile,
}: {
  readonly member: OrgMember | null;
  readonly isAdmin: boolean;
  readonly isSelf?: boolean;
  readonly selfName?: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly selfAvatarSeed?: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onEditMember?: (email: string, patch: MemberEditPatch) => Promise<void>;
  readonly onSyncSelfProfile?: (profile: MorrowUserProfile) => Promise<void>;
}) {
  const { t } = useI18n();
  const { userId } = useMorrowClerkAuth();
  const userProfile = useUserProfile();
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(member?.title ?? "");
  const [draftFunction, setDraftFunction] = useState(member?.functionRole ?? "");
  const [draftAvatarSeed, setDraftAvatarSeed] = useState(avatarSeed ?? selfAvatarSeed ?? "");
  const [draftPortraitImageUrl, setDraftPortraitImageUrl] = useState(userProfile.portraitImageUrl);
  const [draftGrants, setDraftGrants] = useState<MemberGrants>(
    member?.grants ?? {
      vaultManage: false,
      vaultRequest: false,
      billingAccess: false,
      orgKbAccess: false,
    },
  );
  const [pending, setPending] = useState(false);
  const canEditAdmin = isAdmin && onEditMember != null;
  const canEditSelf = isSelf && onSyncSelfProfile != null;
  const canEdit = canEditAdmin || canEditSelf;
  const resolvedAvatarSeed =
    normalizeMorrowDicebearAvatarSeed(draftAvatarSeed) ??
    defaultMorrowDicebearAvatarSeed(
      selfName?.trim() || member?.name?.trim() || member?.email || "",
    );

  useEffect(() => {
    if (!open || !member) return;
    setEditing(false);
    setDraftTitle(isSelf ? userProfile.title || member.title : member.title);
    setDraftFunction(isSelf ? userProfile.role || member.functionRole : member.functionRole);
    setDraftAvatarSeed(avatarSeed ?? selfAvatarSeed ?? "");
    setDraftPortraitImageUrl(userProfile.portraitImageUrl);
    setDraftGrants(member.grants);
    setPending(false);
  }, [
    avatarSeed,
    isSelf,
    member,
    open,
    selfAvatarSeed,
    userProfile.portraitImageUrl,
    userProfile.role,
    userProfile.title,
  ]);

  const handleSubmit = async () => {
    if (!member) return;
    if (isSelf) {
      const profile = writeUserProfile({
        title: draftTitle,
        role: draftFunction,
        avatarSeed: draftAvatarSeed || resolvedAvatarSeed,
        portraitImageUrl: draftPortraitImageUrl,
      });
      upsertTeamMember({
        id: userId ?? LOCAL_SELF_MEMBER_ID,
        name: selfName?.trim() || member.name?.trim() || member.email,
        title: profile.title,
        role: profile.role,
        avatarSeed: profile.avatarSeed,
      });
      if (onSyncSelfProfile) {
        setPending(true);
        try {
          await onSyncSelfProfile(profile);
        } catch {
          // Local profile is already saved; remote sync is best-effort.
        } finally {
          setPending(false);
        }
      }
      onOpenChange(false);
      return;
    }
    if (!onEditMember) return;
    setPending(true);
    try {
      await onEditMember(member.email, {
        title: draftTitle.trim(),
        functionRole: draftFunction.trim(),
        grants: draftGrants,
      });
    } catch {
      // Best-effort; the server is the source of truth and the list re-fetches.
    } finally {
      setPending(false);
    }
    onOpenChange(false);
  };

  const displayName = isSelf
    ? selfName?.trim() || member?.name?.trim() || member?.email || ""
    : member?.name?.trim() || member?.email || "";
  const detailDescription = member
    ? isSelf
      ? t("profile.team.subtitle")
      : member.description.trim() ||
        (canEditAdmin ? t("team.editOtherSubtitle") : t("team.viewOnlyHint"))
    : "";
  const detailInfo: MorrowPersonDetailInfo | null = member ? { email: member.email } : null;
  const detailAvatarSeed = isSelf ? (selfAvatarSeed ?? avatarSeed) : avatarSeed;
  const detailImageUrl = isSelf
    ? draftPortraitImageUrl || userProfile.portraitImageUrl || imageUrl
    : imageUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-sm overflow-hidden">
        {member && detailInfo ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>{displayName}</DialogTitle>
              <DialogDescription>{detailDescription}</DialogDescription>
            </DialogHeader>
            <DialogPanel className="p-5">
              {editing && canEdit ? (
                <div className="flex flex-col gap-5">
                  {isSelf ? (
                    <MorrowPersonAvatarPicker
                      value={resolvedAvatarSeed}
                      portraitImageUrl={draftPortraitImageUrl}
                      disabled={pending}
                      onChange={setDraftAvatarSeed}
                      onPortraitImageUrlChange={setDraftPortraitImageUrl}
                    />
                  ) : (
                    <MemberGrantToggles grants={draftGrants} onChange={setDraftGrants} />
                  )}
                  <MorrowTitleRoleForm
                    name={member.email}
                    title={draftTitle}
                    role={draftFunction}
                    pending={pending}
                    actionsClassName="justify-end"
                    onTitleChange={setDraftTitle}
                    onRoleChange={setDraftFunction}
                    onSubmit={() => void handleSubmit()}
                  />
                </div>
              ) : (
                <MorrowPersonDetailCard
                  name={displayName}
                  title={isSelf ? userProfile.title || member.title : member.title}
                  role={isSelf ? userProfile.role || member.functionRole : member.functionRole}
                  description={detailDescription}
                  info={detailInfo}
                  {...(detailImageUrl ? { imageUrl: detailImageUrl } : {})}
                  {...(detailAvatarSeed ? { avatarSeed: detailAvatarSeed } : {})}
                  canEdit={canEdit}
                  onEdit={() => setEditing(true)}
                />
              )}
            </DialogPanel>
          </>
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}

function MemberDetailDialog({
  member,
  isSelf,
  isAdmin,
  selfName,
  imageUrl,
  avatarSeed,
  open,
  onOpenChange,
  onSyncSelfProfile,
}: {
  readonly member: MorrowTeamMember | null;
  readonly isSelf: boolean;
  readonly isAdmin: boolean;
  readonly selfName: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSyncSelfProfile?: (profile: MorrowUserProfile) => Promise<void>;
}) {
  const { t } = useI18n();
  const { userId } = useMorrowClerkAuth();
  const canEdit = canEditTeamMember({ isSelf, isAdmin });
  const canEditName = canEdit && !isSelf;
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(member?.name ?? "");
  const [draftTitle, setDraftTitle] = useState(member?.title ?? "");
  const [draftRole, setDraftRole] = useState(member?.role ?? "");
  const [draftAvatarSeed, setDraftAvatarSeed] = useState(avatarSeed ?? "");
  const userProfile = useUserProfile();
  const [draftPortraitImageUrl, setDraftPortraitImageUrl] = useState(userProfile.portraitImageUrl);
  const [pending, setPending] = useState(false);
  const resolvedAvatarSeed =
    normalizeMorrowDicebearAvatarSeed(draftAvatarSeed) ?? defaultMorrowDicebearAvatarSeed(selfName);

  useEffect(() => {
    if (!open || !member) return;
    setEditing(false);
    setDraftName(member.name);
    setDraftTitle(member.title);
    setDraftRole(member.role);
    setDraftAvatarSeed(avatarSeed ?? "");
    setDraftPortraitImageUrl(userProfile.portraitImageUrl);
    setPending(false);
  }, [avatarSeed, member, open, userProfile.portraitImageUrl]);

  const handleSubmit = async () => {
    if (!member) return;
    const name = (canEditName ? draftName : member.name).trim() || member.name;
    if (isSelf) {
      const profile = writeUserProfile({
        title: draftTitle,
        role: draftRole,
        avatarSeed: draftAvatarSeed,
        portraitImageUrl: draftPortraitImageUrl,
      });
      upsertTeamMember({
        id: userId ?? LOCAL_SELF_MEMBER_ID,
        name: selfName,
        title: profile.title,
        role: profile.role,
        avatarSeed: profile.avatarSeed,
      });
      if (onSyncSelfProfile) {
        setPending(true);
        try {
          await onSyncSelfProfile(profile);
        } catch {
          // Local profile is already saved; Clerk sync is best-effort.
        } finally {
          setPending(false);
        }
      }
      onOpenChange(false);
      return;
    }
    upsertTeamMember({
      id: member.id,
      name,
      title: draftTitle,
      role: draftRole,
    });
    onOpenChange(false);
  };

  const detailDescription = isSelf
    ? t("profile.team.subtitle")
    : canEdit
      ? t("team.editOtherSubtitle")
      : t("team.viewOnlyHint");
  const detailInfo: MorrowPersonDetailInfo | null = member
    ? {
        email: member.id.includes("@") ? member.id : member.name.includes("@") ? member.name : "",
      }
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-sm overflow-hidden">
        {member && detailInfo ? (
          <>
            <DialogHeader className="sr-only">
              <DialogTitle>{member.name}</DialogTitle>
              <DialogDescription>{detailDescription}</DialogDescription>
            </DialogHeader>
            <DialogPanel className="p-5">
              {editing && canEdit ? (
                <div className="flex flex-col gap-5">
                  {isSelf ? (
                    <MorrowPersonAvatarPicker
                      value={resolvedAvatarSeed}
                      portraitImageUrl={draftPortraitImageUrl}
                      disabled={pending}
                      onChange={setDraftAvatarSeed}
                      onPortraitImageUrlChange={setDraftPortraitImageUrl}
                    />
                  ) : null}
                  <MorrowTitleRoleForm
                    name={draftName}
                    title={draftTitle}
                    role={draftRole}
                    pending={pending}
                    actionsClassName="justify-end"
                    {...(canEditName ? { onNameChange: setDraftName } : {})}
                    onTitleChange={setDraftTitle}
                    onRoleChange={setDraftRole}
                    onSubmit={() => void handleSubmit()}
                  />
                </div>
              ) : (
                <MorrowPersonDetailCard
                  name={member.name}
                  title={member.title}
                  role={member.role}
                  description={detailDescription}
                  info={detailInfo}
                  {...(isSelf && (userProfile.portraitImageUrl || imageUrl)
                    ? { imageUrl: userProfile.portraitImageUrl || imageUrl }
                    : imageUrl
                      ? { imageUrl }
                      : {})}
                  {...(avatarSeed ? { avatarSeed } : {})}
                  canEdit={canEdit}
                  onEdit={() => setEditing(true)}
                />
              )}
            </DialogPanel>
          </>
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}
